/**
 * outlineRaster — pre-rasterizes letter images WITH their outline filter into
 * a cached bitmap URL so the expensive multi-pass `filter: drop-shadow(...)`
 * chains never run during gameplay.
 *
 * Why: each YellowLetter/brand letter used a CSS filter with 8-11 chained
 * drop-shadows. With 20+ letters on screen, every repaint re-executed the
 * chain — measured at 300-900 ms per paint on software-rendered machines,
 * collapsing gameplay to ~2 fps. Rasterizing ONCE per (image, filter) pair
 * makes every subsequent paint a cheap bitmap composite → steady 60 fps.
 *
 * Jun 2026 — two fixes after profiling a VS HUMANO duel:
 *  1. A chain of N drop-shadows costs 2^N composites when rendered through
 *     `ctx.filter` (12 passes ≈ 0.75 s, 24 passes > 30 s per letter). We now
 *     emulate the exact same chain ourselves, one pass at a time (3 draws per
 *     pass, linear) — same pixels, ~100× faster.
 *  2. The work runs in a Web Worker on an OffscreenCanvas so the main thread
 *     never blocks (clicks / WebSocket messages were being processed seconds
 *     late). Falls back to the main thread when workers/OffscreenCanvas are
 *     unavailable, and to the live CSS filter when canvas filters are.
 */
import { useEffect, useState } from "react";

const cache = new Map(); // key -> {url|null, scale}
const pending = new Set();
const listeners = new Map(); // key -> Set<cb>

// Main-thread fallback queue: at most ONE rasterization per macrotask so the
// browser can paint / run timers between jobs.
const rasterQueue = [];
let rasterRunning = false;
let rasterPausedUntil = 0;

/** Temporarily suspend the (main-thread) raster queue so time-critical UI
 *  transitions aren't janked. No-op for the worker path. */
export function pauseRasterQueue(ms = 800) {
  rasterPausedUntil = Math.max(rasterPausedUntil, Date.now() + ms);
  if (rasterQueue.length) pumpRasterQueue();
}

function pumpRasterQueue() {
  if (rasterRunning) return;
  const wait = rasterPausedUntil - Date.now();
  if (wait > 0) {
    rasterRunning = true;
    setTimeout(() => { rasterRunning = false; pumpRasterQueue(); }, wait);
    return;
  }
  const job = rasterQueue.shift();
  if (!job) return;
  rasterRunning = true;
  setTimeout(() => {
    try { job(); } finally {
      rasterRunning = false;
      if (rasterQueue.length) pumpRasterQueue();
    }
  }, 0);
}

function maxFilterPx(filter) {
  let max = 0;
  const re = /(-?\d*\.?\d+)px/g;
  let m;
  while ((m = re.exec(filter))) max = Math.max(max, Math.abs(parseFloat(m[1])));
  return max || 2;
}

function notify(key, entry) {
  cache.set(key, entry);
  pending.delete(key);
  const subs = listeners.get(key);
  if (subs) {
    subs.forEach((f) => f(entry));
    listeners.delete(key);
  }
}

// ---------------------------------------------------------------------------
// Filter parsing: "drop-shadow(x y [blur] color) ..." → [{dx, dy, blur, color}]
// (px values already scaled to canvas space by the caller).
function parseDropShadows(filter) {
  const passes = [];
  let i = 0;
  const s = String(filter || "");
  while (i < s.length) {
    const start = s.indexOf("drop-shadow(", i);
    if (start < 0) break;
    let j = start + "drop-shadow(".length;
    let depth = 1;
    while (j < s.length && depth > 0) {
      if (s[j] === "(") depth++;
      else if (s[j] === ")") depth--;
      j++;
    }
    const inner = s.slice(start + "drop-shadow(".length, j - 1);
    // Split on whitespace outside parentheses.
    const parts = [];
    let cur = "";
    depth = 0;
    for (const ch of inner) {
      if (ch === "(") depth++;
      if (ch === ")") depth--;
      if (/\s/.test(ch) && depth === 0) {
        if (cur) parts.push(cur);
        cur = "";
      } else cur += ch;
    }
    if (cur) parts.push(cur);
    const nums = [];
    let color = "#000";
    for (const p of parts) {
      if (/^-?\d*\.?\d+(px)?$/.test(p)) nums.push(parseFloat(p));
      else color = p;
    }
    passes.push({ dx: nums[0] || 0, dy: nums[1] || 0, blur: nums[2] || 0, color });
    i = j;
  }
  return passes;
}

// Applies the drop-shadow chain to `acc` (a canvas already holding the
// source image) EXACTLY like CSS does — each pass puts a tinted, offset,
// blurred silhouette of the accumulated result UNDER it — but with 3 draws
// per pass instead of the 2^N composites the browser filter pipeline uses.
// Written in plain ES5 so `.toString()` can be embedded into the worker.
function applyShadowPasses(acc, passes, makeCanvas) {
  var W = acc.width, H = acc.height;
  var tmp = makeCanvas(W, H);
  var t = tmp.getContext("2d");
  var a = acc.getContext("2d");
  var hasFilter = "filter" in t;
  for (var k = 0; k < passes.length; k++) {
    var p = passes[k];
    t.globalCompositeOperation = "source-over";
    t.clearRect(0, 0, W, H);
    if (hasFilter) t.filter = p.blur > 0 ? "blur(" + (p.blur / 2) + "px)" : "none";
    t.drawImage(acc, p.dx, p.dy);
    if (hasFilter) t.filter = "none";
    t.globalCompositeOperation = "source-in";
    t.fillStyle = p.color;
    t.fillRect(0, 0, W, H);
    a.globalCompositeOperation = "destination-over";
    a.drawImage(tmp, 0, 0);
  }
  a.globalCompositeOperation = "source-over";
}

// Shared geometry for both the worker and the main-thread paths.
function rasterGeometry(img, filter, nominal) {
  const SS = 2; // supersample so the result stays crisp on retina
  const natW = img.naturalWidth || img.width || 1;
  const natH = img.naturalHeight || img.height || 1;
  const s = (nominal * SS) / Math.max(natW, natH);
  const w = Math.max(1, Math.round(natW * s));
  const h = Math.max(1, Math.round(natH * s));
  const pad = Math.ceil(maxFilterPx(filter) * SS * 3) + 4;
  // CSS filter px are in layout space; scale them to canvas space (SS×).
  const scaledFilter = filter.replace(/(-?\d*\.?\d+)px/g, (mm, n) => `${parseFloat(n) * SS}px`);
  const maxDim = Math.max(w, h);
  return { w, h, pad, passes: parseDropShadows(scaledFilter), scale: (maxDim + pad * 2) / maxDim };
}

// ---- Main-thread path (fallback) -------------------------------------------
function doRasterize(img, key, filter, nominal) {
  try {
    const { w, h, pad, passes, scale } = rasterGeometry(img, filter, nominal);
    const makeCanvas = (W, H) => {
      const c = document.createElement("canvas");
      c.width = W; c.height = H;
      return c;
    };
    const canvas = makeCanvas(w + pad * 2, h + pad * 2);
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, pad, pad, w, h);
    applyShadowPasses(canvas, passes, makeCanvas);
    notify(key, { url: canvas.toDataURL("image/png"), scale });
  } catch (e) {
    notify(key, { url: null, scale: 1 });
  }
}

// ---- Worker path -------------------------------------------------------------
const WORKER_SRC = `
${applyShadowPasses.toString()}
self.onmessage = function (ev) {
  var d = ev.data;
  try {
    var makeCanvas = function (W, H) { return new OffscreenCanvas(W, H); };
    var canvas = makeCanvas(d.w + d.pad * 2, d.h + d.pad * 2);
    var ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(d.bitmap, d.pad, d.pad, d.w, d.h);
    if (d.bitmap.close) d.bitmap.close();
    applyShadowPasses(canvas, d.passes, makeCanvas);
    canvas.convertToBlob({ type: "image/png" })
      .then(function (blob) { self.postMessage({ id: d.id, blob: blob }); })
      .catch(function () { self.postMessage({ id: d.id, failed: true }); });
  } catch (e) {
    self.postMessage({ id: d.id, failed: true });
  }
};
`;

let worker = null;
let workerBroken = false;
let nextJobId = 1;
const workerJobs = new Map(); // id -> { img, key, filter, nominal, scale }

function requeueOnMain(job) {
  rasterQueue.push(() => doRasterize(job.img, job.key, job.filter, job.nominal));
  pumpRasterQueue();
}

function getWorker() {
  if (workerBroken) return null;
  if (worker) return worker;
  try {
    if (typeof Worker === "undefined" || typeof OffscreenCanvas === "undefined" || typeof createImageBitmap !== "function") {
      workerBroken = true;
      return null;
    }
    const blob = new Blob([WORKER_SRC], { type: "application/javascript" });
    worker = new Worker(URL.createObjectURL(blob));
    worker.onmessage = (ev) => {
      const { id, blob: png, failed } = ev.data || {};
      const job = workerJobs.get(id);
      if (!job) return;
      workerJobs.delete(id);
      if (failed || !png) { requeueOnMain(job); return; }
      notify(job.key, { url: URL.createObjectURL(png), scale: job.scale });
    };
    worker.onerror = () => {
      workerBroken = true;
      workerJobs.forEach(requeueOnMain);
      workerJobs.clear();
      try { worker.terminate(); } catch { /* */ }
      worker = null;
    };
    return worker;
  } catch (e) {
    workerBroken = true;
    return null;
  }
}

function rasterizeOffThread(img, key, filter, nominal) {
  const wk = getWorker();
  if (!wk) return false;
  const geo = rasterGeometry(img, filter, nominal);
  const id = nextJobId++;
  const job = { img, key, filter, nominal, scale: geo.scale };
  workerJobs.set(id, job);
  createImageBitmap(img)
    .then((bitmap) => {
      wk.postMessage({ id, bitmap, w: geo.w, h: geo.h, pad: geo.pad, passes: geo.passes }, [bitmap]);
    })
    .catch(() => {
      workerJobs.delete(id);
      requeueOnMain(job);
    });
  return true;
}

export function rasterizeOutline(src, filter, nominal = 64) {
  const key = `${src}|${filter}|${nominal}`;
  const hit = cache.get(key);
  if (hit) return hit;
  if (pending.has(key)) return null;
  pending.add(key);
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    if (rasterizeOffThread(img, key, filter, nominal)) return;
    rasterQueue.push(() => doRasterize(img, key, filter, nominal));
    pumpRasterQueue();
  };
  img.onerror = () => notify(key, { url: null, scale: 1 });
  img.src = src;
  return null;
}

/** Promise that resolves when the (src, filter, nominal) raster is cached.
 *  Resolves on ALL outcomes (success, unsupported browser, load error) so
 *  callers can safely `await` it to gate game start on a cold cache. */
export function waitForOutline(src, filter, nominal = 64) {
  const key = `${src}|${filter}|${nominal}`;
  const hit = cache.get(key);
  if (hit) return Promise.resolve(hit);
  return new Promise((resolve) => {
    if (!listeners.has(key)) listeners.set(key, new Set());
    listeners.get(key).add(resolve);
    rasterizeOutline(src, filter, nominal);
  });
}

// Diagnostics (read-only) — window.__outlineRasterStats() in DevTools.
if (typeof window !== "undefined") {
  window.__outlineRasterStats = () => ({
    cached: cache.size,
    cachedOk: [...cache.values()].filter((e) => e && e.url).length,
    pending: pending.size,
    workerJobs: workerJobs.size,
    workerBroken,
    queue: rasterQueue.length,
  });
}

export function useOutlinedImage(src, filter, nominal = 64) {
  const key = `${src}|${filter}|${nominal}`;
  const [entry, setEntry] = useState(() => cache.get(key) || null);
  useEffect(() => {
    const hit = cache.get(key);
    if (hit) {
      setEntry(hit);
      return undefined;
    }
    let on = true;
    const cb = (e) => { if (on) setEntry(e); };
    if (!listeners.has(key)) listeners.set(key, new Set());
    listeners.get(key).add(cb);
    rasterizeOutline(src, filter, nominal);
    return () => {
      on = false;
      const s = listeners.get(key);
      if (s) s.delete(cb);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return entry;
}
